/**
 * PlantillasFactura.jsx
 *
 * Módulo de generación de HTML para imprimir facturas / tickets / notas.
 *
 * Contiene 5 plantillas:
 *   - 2 plantillas de 80mm para impresoras térmicas POS.
 *   - 3 plantillas de 8.5x11 pulgadas (tamaño carta).
 *
 * Cada plantilla recibe los datos de la factura y retorna HTML optimizado para impresión.
 * Es un módulo puro: no consume contexto, ni hace llamadas al backend, ni renderiza UI,
 * por lo que las reglas de seguridad / diseño FinancePro se aplican en los componentes
 * que invocan estas funciones (ver `FacturaPOS.jsx`, `VistaFactura.jsx`, etc.).
 */

import {
  cssQrCarta,
  envolverCierreCarta,
  scriptQrCartaSinSalto,
  formatoFechaCorto,
  formatoFechaLargo,
  escHtml,
} from './documentoEmpresarial';

/**
 * Datos esperados en facturaData:
 * {
 *   items: Array,
 *   impuestos: Array,
 *   subtotal: Number,
 *   totalDescuentos: Number,
 *   totalImpuestos: Number,
 *   total: Number,
 *   documento: String,
 *   numero: String,
 *   fecha: String,
 *   ncf: String,
 *   tipoDocumento: String,
 *   observaciones: String,
 *   empresa: Object/String
 * }
 */

/** Resuelve nombre / dirección / teléfono / RNC / email igual en todas las plantillas. */
const resolverDatosEmpresa = (empresa, configuracion = {}) => {
  const config = configuracion || {};
  const empresaObj =
    empresa && typeof empresa === 'object' && !Array.isArray(empresa) ? empresa : {};
  const nombreDesdeEmpresa =
    typeof empresa === 'string'
      ? empresa
      : empresaObj.empresa || empresaObj.nombre || empresaObj.name || '';
  return {
    mostrarInfoEmpresa: config.mostrarInfoEmpresa !== false,
    empresaNombre: (config.nombreEmpresa || nombreDesdeEmpresa || 'Empresa').trim() || 'Empresa',
    empresaDireccion: (
      config.direccionEmpresa ||
      empresaObj.direccion ||
      empresaObj.direccion_empresa ||
      ''
    ).trim(),
    empresaTelefono: (
      config.telefonoEmpresa ||
      empresaObj.telefono ||
      empresaObj.telefono_empresa ||
      ''
    ).trim(),
    empresaRNC: (config.rncEmpresa || empresaObj.rnc || empresaObj.rnc_empresa || '').trim(),
    empresaEmail: (config.emailEmpresa || empresaObj.email || empresaObj.correo || '').trim(),
  };
};

const RNC_CONSUMIDOR_GENERICO = '999999';

const resolverRncComprador = (facturaData = {}) => {
  const candidatos = [facturaData.rnc, facturaData.rnc_comprador, facturaData.suplidor];
  for (const c of candidatos) {
    const v = String(c || '').trim();
    if (v && v !== RNC_CONSUMIDOR_GENERICO) return v;
  }
  return '';
};

const resolverNombreCliente = (facturaData = {}) =>
  String(
    facturaData.cliente_nombre ||
      facturaData.nombre_cliente ||
      facturaData.razonsocial ||
      ''
  ).trim();

const htmlClienteTicket = (facturaData, estilo = 'margin:1px 0;') => {
  const rncComprador = resolverRncComprador(facturaData);
  const nombre = resolverNombreCliente(facturaData);
  const partes = [];
  if (nombre) partes.push(`<div style="${estilo}">Cliente: ${nombre}</div>`);
  if (rncComprador) partes.push(`<div style="${estilo}">RNC: ${rncComprador}</div>`);
  return partes.join('');
};

const toNumberTicket = (value) => parseFloat(String(value ?? '').replace(/,/g, '')) || 0;
const round2Ticket = (value) => Math.round((Number(value) + Number.EPSILON) * 100) / 100;
/** Formato miles con coma y 2 decimales (igual que fac1030 / PDF térmico). */
const fmtTicket = (value) =>
  (Number(value) || 0).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

/**
 * Tipo de comprobante e-CF a partir del e-NCF / código, igual que
 * imprimirfacturasfiscalthermalitbincluido (fac1030).
 */
const derivarComprobanteTicket = (facturaData = {}) => {
  const ncf = String(facturaData.ncf || facturaData.comprobante || '').toUpperCase();
  const codigo = String(facturaData.codigocomprobante ?? '').trim();
  const prefijo = ncf.slice(0, 3);
  let tipo = '';
  let esConsumo = false;
  let esEspecial = false;
  if (prefijo === 'E31' || codigo === '31') {
    tipo = 'Factura de Credito Fiscal Electronica';
  } else if (prefijo === 'E32' || codigo === '32') {
    tipo = 'Factura de Consumo Electronico';
    esConsumo = true;
  } else if (prefijo === 'E44' || codigo === '44') {
    tipo = 'Comprobante Electronico para Regimenes Especiales';
    esEspecial = true;
  } else if (prefijo === 'E45' || codigo === '45') {
    tipo = 'Comprobante Electronico Gubernamental';
  } else {
    tipo =
      String(facturaData.tipoDocumento || facturaData.tipocomprobante || '')
        .split(':')[0]
        ?.trim() || 'FACTURA POS';
  }
  return { tipo, esConsumo, esEspecial, ncf };
};

/**
 * ITBIS de línea: en reimpresión histfact.itbis es monto; en POS item.impuesto
 * suele ser la tasa (%). Si impuesto > 100 se trata como monto.
 */
/** Segunda línea bajo la descripción en los formatos de hoja (p. ej. "DUEÑO: …"). */
const subdescripcionHtml = (item) => {
  const texto = String(item?.subdescripcion || '').trim();
  if (!texto) return '';
  const seguro = texto.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);
  return `<div style="font-size:0.85em;color:#555;margin-top:2px;">${seguro}</div>`;
};

const itbisLineaTicket = (item, base, esEspecial) => {
  if (esEspecial) return 0;
  const montoExpl = toNumberTicket(item.itbis);
  const tasaOMonto = toNumberTicket(item.tasaitbis ?? item.impuesto);
  if (montoExpl > 0 && (tasaOMonto === 0 || Math.abs(montoExpl - tasaOMonto) > 0.001)) {
    return round2Ticket(montoExpl);
  }
  if (tasaOMonto > 100) return round2Ticket(tasaOMonto);
  if (tasaOMonto > 0) return round2Ticket(base * (tasaOMonto / 100));
  return 0;
};

/**
 * HTML del ticket 80mm idéntico a factura31-209.pdf / fac1030 térmico:
 * encabezado centrado, COD/ITEM/PRECIO/ITBIS/TOTAL, totales SUB-TOTAL…DEVUELTO,
 * QR DGII y vendedor. Se imprime como HTML monospace (white-space: pre).
 */
const construirHtmlTicket80mm = (facturaData) => {
  const {
    items = [],
    totalDescuentos = 0,
    totalImpuestos = 0,
    total = 0,
    documento = '',
    numero = '',
    fecha = '',
    observaciones = '',
    empresa = {},
    configuracion = {},
  } = facturaData;

  const config = configuracion || {};
  const {
    mostrarInfoEmpresa,
    empresaNombre,
    empresaDireccion,
    empresaTelefono,
    empresaRNC,
  } = resolverDatosEmpresa(empresa, config);
  const mostrarNCF = config.mostrarNCF !== false;
  const mostrarObservaciones = config.mostrarObservaciones !== false && observaciones;

  const comp = derivarComprobanteTicket(facturaData);
  const esElectronico = Boolean(facturaData.mostrarComprobanteElectronico);
  const rncComprador = resolverRncComprador(facturaData);
  const nombreCliente = resolverNombreCliente(facturaData);

  const envio = toNumberTicket(facturaData.envio);
  const otrosCargos = toNumberTicket(facturaData.otroscargos);
  const descuentoGeneral = toNumberTicket(facturaData.descuentogeneral);
  const totalImporte = items.reduce(
    (sum, it) =>
      sum + toNumberTicket(it.cantidad) * toNumberTicket(it.valor ?? it.precio),
    0
  );

  let subtotalCalc = 0;
  let totalItbisCalc = 0;
  let descuentoTotalCalc = 0;
  let sumaBases = 0;

  const lineasBase = items.map((it) => {
    const cantidad = toNumberTicket(it.cantidad);
    const valor = toNumberTicket(it.valor ?? it.precio);
    const descuentoLinea =
      toNumberTicket(it.descuento) + toNumberTicket(it.descuento_general);
    const importe = round2Ticket(cantidad * valor);
    const descuentoProrrateado = totalImporte
      ? round2Ticket(descuentoGeneral * (importe / totalImporte))
      : 0;
    const descuentoItem = round2Ticket(descuentoLinea + descuentoProrrateado);
    const base = round2Ticket(importe - descuentoItem);
    const itbis = itbisLineaTicket(it, base, comp.esEspecial);

    subtotalCalc += importe;
    totalItbisCalc += itbis;
    descuentoTotalCalc += descuentoItem;
    sumaBases += base;

    return {
      codigo: String(it.chasis || it.codigo || '').trim(),
      descripcion: String(it.item || it.descripcion || '').trim().slice(0, 25),
      subdescripcion: String(it.subdescripcion || '').trim(),
      cantidad,
      valor,
      base,
      itbis,
    };
  });

  subtotalCalc = round2Ticket(subtotalCalc);
  totalItbisCalc = round2Ticket(totalItbisCalc);
  descuentoTotalCalc = round2Ticket(
    descuentoTotalCalc || toNumberTicket(totalDescuentos)
  );
  const itbisFactura = toNumberTicket(totalImpuestos);
  // Si las líneas no traen tasa/monto pero la factura sí tiene ITBIS, prorratear.
  if (totalItbisCalc === 0 && itbisFactura > 0 && sumaBases > 0 && !comp.esEspecial) {
    let acum = 0;
    lineasBase.forEach((l, idx) => {
      if (idx === lineasBase.length - 1) {
        l.itbis = round2Ticket(itbisFactura - acum);
      } else {
        l.itbis = round2Ticket(itbisFactura * (l.base / sumaBases));
        acum = round2Ticket(acum + l.itbis);
      }
    });
    totalItbisCalc = itbisFactura;
  } else if (totalItbisCalc === 0 && itbisFactura > 0) {
    totalItbisCalc = itbisFactura;
  }

  const lineas = lineasBase.map((l) => ({
    codigo: l.codigo,
    descripcion: l.descripcion,
    subdescripcion: l.subdescripcion,
    cantidad: l.cantidad,
    precioConItbis: round2Ticket(l.valor + (l.cantidad ? l.itbis / l.cantidad : 0)),
    itbis: l.itbis,
    total: round2Ticket(l.valor * l.cantidad + l.itbis),
  }));

  const pagos = (facturaData.pago && facturaData.pago.pagos) || {};
  const notaCreditoPago = toNumberTicket(
    typeof pagos.nota_credito === 'number'
      ? pagos.nota_credito
      : pagos.nota_credito && pagos.nota_credito.monto
  );
  const totalNotaCredito = toNumberTicket(
    facturaData.totalNotaCredito ?? facturaData.total_notas_credito ?? notaCreditoPago
  );
  const totalPagado = toNumberTicket(facturaData.pago && facturaData.pago.totalPagado);
  const totalCalc = round2Ticket(
    subtotalCalc + totalItbisCalc - totalNotaCredito + otrosCargos + envio - descuentoTotalCalc
  );
  const totalMostrar =
    facturaData.totalAPagar != null
      ? round2Ticket(facturaData.totalAPagar)
      : toNumberTicket(total) > 0
        ? round2Ticket(Math.max(0, toNumberTicket(total) - totalNotaCredito))
        : totalCalc;
  const subtotalMostrar =
    subtotalCalc > 0 ? subtotalCalc : round2Ticket(toNumberTicket(facturaData.subtotal) + descuentoTotalCalc);
  const devuelto =
    facturaData.devuelto != null
      ? round2Ticket(facturaData.devuelto)
      : totalPagado > totalMostrar
        ? round2Ticket(totalPagado - totalMostrar)
        : 0;

  const ANCHO = 42;
  const centrar = (t) => {
    const s = String(t || '');
    if (s.length >= ANCHO) return s.slice(0, ANCHO);
    const pad = Math.floor((ANCHO - s.length) / 2);
    return ' '.repeat(pad) + s;
  };
  const totalLinea = (label, valor) => {
    const izq = '  ' + label.padEnd(12, ' ');
    const der = fmtTicket(valor);
    const espacio = Math.max(1, ANCHO - izq.length - der.length);
    return izq + ' '.repeat(espacio) + der;
  };
  const separador = '-'.repeat(ANCHO);
  const L = [];

  if (mostrarInfoEmpresa) {
    L.push({ t: centrar(empresaNombre || 'Empresa'), b: true });
    if (empresaTelefono) L.push({ t: centrar(empresaTelefono) });
    if (empresaDireccion) L.push({ t: centrar(empresaDireccion) });
    if (empresaRNC) L.push({ t: centrar('RNC: ' + empresaRNC) });
  }
  L.push({ t: '' });
  L.push({ t: centrar(formatoFechaCorto(fecha)) });


  
  if (mostrarNCF) {
    L.push({ t: comp.tipo });
    if (comp.ncf) L.push({ t: 'E-NCF: ' + comp.ncf });
  }
  
  if (!comp.esConsumo || (comp.esConsumo && totalMostrar > 250000)) {
    if (rncComprador) L.push({ t: 'RNC     ' + rncComprador });
    if (nombreCliente) L.push({ t: 'CLIENTE ' + nombreCliente });
  }

  if (esElectronico && facturaData.fechaVencimiento) {
    L.push({ t: 'Fecha vencimiento ' + facturaData.fechaVencimiento });
  }
  L.push({ t: '' });

  L.push({ t: separador });
  L.push({ t: 'COD.     ITEM        PRECIO  ITBIS   TOTAL', b: true });
  L.push({ t: separador });
  if (lineas.length === 0) {
    L.push({ t: centrar('Sin ítems') });
  } else {
    lineas.forEach((l) => {
      if (l.codigo) L.push({ t: l.codigo });
      L.push({ t: String(l.cantidad) + '  ' + (l.descripcion || '—') });
      if (l.subdescripcion) {
        l.subdescripcion.match(new RegExp(`.{1,${ANCHO}}`, 'g'))?.forEach((t) => L.push({ t }));
      }
      const cols =
        fmtTicket(l.precioConItbis).padStart(14, ' ') +
        fmtTicket(l.itbis).padStart(10, ' ') +
        fmtTicket(l.total).padStart(12, ' ');
      L.push({ t: cols.padStart(ANCHO, ' ') });
    });
  }
  L.push({ t: separador });

  L.push({ t: totalLinea('SUB-TOTAL', subtotalMostrar) });
  L.push({ t: totalLinea('TRANSP.', envio) });
  L.push({ t: totalLinea('OTROS', otrosCargos) });
  L.push({ t: totalLinea('DESCUENTO', descuentoTotalCalc) });
  L.push({ t: totalLinea('ITBIS', totalItbisCalc) });
  if (totalNotaCredito !== 0) L.push({ t: totalLinea('N. CRED', totalNotaCredito) });
  L.push({ t: totalLinea('TOTAL', totalMostrar), b: true });
  L.push({ t: totalLinea('DEVUELTO', devuelto) });
  L.push({ t: '' });
  L.push({ t: separador });
  L.push({ t: '' });

  const msgDevolucion =
    (config.mensajeDevolucion || '').trim() || '*****No se aceptan devoluciones*****';
  L.push({ t: centrar(msgDevolucion) });
  if (config.mensajeGracias) L.push({ t: centrar(config.mensajeGracias) });
  if (config.mensajeGarantia) {
    L.push({ t: '' });
    String(config.mensajeGarantia)
      .match(new RegExp(`.{1,${ANCHO}}`, 'g'))
      ?.forEach((linea) => L.push({ t: centrar(linea) }));
  }
  if (config.leyendaPiePagina) {
    L.push({ t: '' });
    String(config.leyendaPiePagina)
      .match(new RegExp(`.{1,${ANCHO}}`, 'g'))
      ?.forEach((linea) => L.push({ t: centrar(linea) }));
  }
  if (mostrarObservaciones) {
    L.push({ t: '' });
    L.push({ t: 'Observaciones:' });
    String(observaciones)
      .match(new RegExp(`.{1,${ANCHO}}`, 'g'))
      ?.forEach((linea) => L.push({ t: linea }));
  }

  if (esElectronico && facturaData.qrDataUrl) {
    L.push({ t: '' });
    L.push({ qr: true });
    if (facturaData.codigoSeguridad) {
      L.push({ t: centrar('Código Seg: ' + facturaData.codigoSeguridad) });
    }
    if (facturaData.fechaFirma) {
      L.push({ t: centrar('Fecha Firma: ' + facturaData.fechaFirma) });
    }
  }

  L.push({ t: '' });
  L.push({ t: separador });
  const vendedor = String(
    facturaData.usuario || facturaData.vendedor || facturaData.usuarioLog || ''
  ).trim();
  if (vendedor) L.push({ t: centrar(vendedor) });

  const titulo = `${documento || 'Factura'}${documento && numero ? '-' : ''}${numero || comp.ncf || ''}`;
  const cuerpo = L.map((l) => {
    if (l.qr) {
      return `<div class="qr"><img src="${facturaData.qrDataUrl}" width="150" height="150" alt="QR DGII" /></div>`;
    }
    return `<div${l.b ? ' class="b"' : ''}>${escHtml(l.t) || '&nbsp;'}</div>`;
  }).join('');

  return `<!DOCTYPE html>
<html lang="es">
<head>
<meta charset="utf-8" />
<title>${escHtml(titulo)}</title>
<style>
  @page { size: 80mm auto; margin: 0; }
  * { box-sizing: border-box; }
  @media print {
    body { margin: 0 !important; padding: 4mm 3mm !important; }
  }
  body { margin: 0; padding: 4mm 3mm; }
  #recibo {
    width: 74mm;
    font-family: "Consolas", "Courier New", monospace;
    font-size: 11px;
    line-height: 1.35;
    color: #000;
    white-space: pre;
  }
  #recibo .b { font-weight: bold; }
  #recibo .qr { display: flex; justify-content: center; margin: 6px 0; white-space: normal; }
  #recibo .qr img { width: 40mm; height: 40mm; }
</style>
</head>
<body>
  <div id="recibo">${cuerpo}</div>
</body>
</html>`;
};

/**
 * PLANTILLA 1: Ticket térmico 80mm — layout fac1030 / factura electrónica DGII
 */
export const plantilla80mmBasica = (facturaData) => construirHtmlTicket80mm(facturaData);

/**
 * PLANTILLA 2: Ticket térmico 80mm — mismo layout (separadores del PDF)
 */
export const plantilla80mmConLineas = (facturaData) => construirHtmlTicket80mm(facturaData);

/**
 * PLANTILLA 3: Formato carta 8.5x11 - Estilo profesional con encabezado
 */
export const plantillaCartaProfesional = (facturaData) => {
  const {
    items = [],
    impuestos = [],
    subtotal = 0,
    totalDescuentos = 0,
    totalImpuestos = 0,
    total = 0,
    documento = '',
    numero = '',
    fecha = '',
    ncf = '',
    tipoDocumento = '',
    observaciones = '',
    empresa = {},
    configuracion = {},
    cliente_nombre = '',
  } = facturaData;

  const totalNotaCredito = facturaData.totalNotaCredito ?? facturaData.total_notas_credito ?? 0;
  const totalAPagar = facturaData.totalAPagar ?? Math.max(0, Number(total) - Number(totalNotaCredito));

  const config = configuracion || {};
  const {
    mostrarInfoEmpresa,
    empresaNombre,
    empresaDireccion,
    empresaTelefono,
    empresaRNC,
    empresaEmail,
  } = resolverDatosEmpresa(empresa, config);
  const mostrarNCF = config.mostrarNCF !== false;
  const mostrarObservaciones = config.mostrarObservaciones !== false && observaciones;
  const mostrarDescuentos = config.mostrarDescuentos !== false;
  const mostrarImpuestos = config.mostrarImpuestos !== false;
  const mostrarSubtotalAntesDescuentos = config.mostrarSubtotalAntesDescuentos !== false;

  const formatoFecha = formatoFechaLargo;

  const itemsHtml = items.length === 0 
    ? '<tr><td colspan="6" style="text-align:center;padding:20px;">Sin ítems</td></tr>'
    : items.map(item => `
        <tr>
          <td style="padding:8px;border-bottom:1px solid #ddd;">${String(item.codigo || '—')}</td>
          <td style="padding:8px;border-bottom:1px solid #ddd;">${String(item.descripcion || '—')}${subdescripcionHtml(item)}</td>
          <td style="text-align:center;padding:8px;border-bottom:1px solid #ddd;">${parseFloat(item.cantidad || 0)}</td>
          <td style="text-align:right;padding:8px;border-bottom:1px solid #ddd;">$${parseFloat(item.precio || 0).toFixed(2)}</td>
          <td style="text-align:right;padding:8px;border-bottom:1px solid #ddd;">$${parseFloat(item.descuento || 0).toFixed(2)}</td>
          <td style="text-align:right;padding:8px;border-bottom:1px solid #ddd;font-weight:600;">$${parseFloat(item.subtotal || 0).toFixed(2)}</td>
        </tr>
      `).join('');

  const impuestosHtml = impuestos.length > 0
    ? impuestos.map(imp => `
        <tr>
          <td colspan="5" style="text-align:right;padding:8px;border-bottom:1px solid #ddd;">${imp.nombre}:</td>
          <td style="text-align:right;padding:8px;border-bottom:1px solid #ddd;">$${Number(imp.monto || 0).toFixed(2)}</td>
        </tr>
      `).join('')
    : '';

  const subtotalAntesDescuentos = items.reduce(
    (s, i) => s + parseFloat(i.cantidad || 0) * parseFloat(i.precio || 0),
    0
  );

  
  return `
    <!DOCTYPE html>
    <html>
      <head>
        <title>Factura ${documento}-${numero}</title>
        <style>
          @page {
            size: letter;
            margin: 20mm;
          }
          @media print {
            body { 
              margin: 0 !important;
              padding: 0 !important;
            }
            * { box-sizing: border-box; }
          }
          ${cssQrCarta}
          body { 
            font-family: Arial, sans-serif;
            font-size: 12px;
            padding: 20mm;
            margin: 0;
            color: #333;
            line-height: 1.6;
          }
          .encabezado {
            border-bottom: 3px solid #333;
            padding-bottom: 20px;
            margin-bottom: 30px;
          }
          .tabla-encabezado {
            width: 100%;
            border-collapse: collapse;
            margin: 0 0 12px 0;
          }
          .tabla-encabezado td {
            vertical-align: top;
            border: none;
            padding: 0;
          }
          table.items {
            width: 100%;
            border-collapse: collapse;
            margin: 20px 0;
          }
          table.items th {
            background-color: #f5f5f5;
            padding: 12px;
            text-align: left;
            font-weight: bold;
            border-bottom: 2px solid #333;
          }
          .totales {
            margin-top: 30px;
            text-align: right;
          }
          .cierre-doc .totales { margin-top: 0; }
          .total-final {
            font-size: 18px;
            font-weight: bold;
            margin-top: 10px;
            padding-top: 10px;
            border-top: 2px solid #333;
          }
        </style>
      </head>
      <body>
        <div class="encabezado">
          <table class="tabla-encabezado">
            <tr>
              <td style="width:58%;text-align:left;">
                ${mostrarInfoEmpresa ? `
                  <div style="font-size:26px;font-weight:700;color:#222;margin:0 0 8px 0;line-height:1.2;">${empresaNombre}</div>
                  ${empresaDireccion ? `<div style="margin:2px 0;font-size:12px;color:#555;">${empresaDireccion}</div>` : ''}
                  ${empresaTelefono ? `<div style="margin:2px 0;font-size:11px;color:#666;">Tel: ${empresaTelefono}</div>` : ''}
                  ${empresaRNC ? `<div style="margin:2px 0;font-size:11px;color:#666;">RNC: ${empresaRNC}</div>` : ''}
                  ${empresaEmail ? `<div style="margin:2px 0;font-size:11px;color:#666;">${empresaEmail}</div>` : ''}
                ` : '&nbsp;'}
              </td>
              <td style="width:42%;text-align:right;font-size:12px;line-height:1.55;color:#444;">
                <div style="font-size:15px;font-weight:700;color:#222;margin-bottom:6px;letter-spacing:0.04em;text-transform:uppercase;">${(tipoDocumento.split(':')[0] || 'FACTURA').trim()}</div>
                ${documento || numero ? `<div style="font-size:18px;font-weight:700;color:#111;margin-bottom:10px;">${documento}${documento && numero ? '-' : ''}${numero}</div>` : ''}
                <div>Fecha: ${formatoFecha(fecha)}</div>
                ${mostrarNCF && ncf ? `<div>Comprobante (NCF): ${ncf}</div>` : ''}
                ${facturaData.mostrarComprobanteElectronico && facturaData.fechaVencimiento ? `<div>Fecha de validez: ${facturaData.fechaVencimiento}</div>` : ''}
              </td>
            </tr>
          </table>
          ${htmlClienteTicket(facturaData)
            ? `<div style="margin-top:14px;padding-top:10px;border-top:1px solid #e0e0e0;text-align:left;font-size:13px;">${htmlClienteTicket(facturaData, 'margin:2px 0;')}</div>`
            : ''}
        </div>
        <table class="items">
          <thead>
            <tr>
              <th>Código</th>
              <th>Descripción</th>
              <th style="text-align:center;">Cantidad</th>
              <th style="text-align:right;">Precio Unit.</th>
              <th style="text-align:right;">Descuento</th>
              <th style="text-align:right;">Subtotal</th>
            </tr>
          </thead>
          <tbody>
            ${itemsHtml}
            ${mostrarImpuestos ? impuestosHtml : ''}
          </tbody>
        </table>
        ${envolverCierreCarta(
          `<div class="totales">
          ${mostrarSubtotalAntesDescuentos ? `<div style="margin-bottom:5px;">Subtotal (antes descuentos): <strong>$${Number(subtotalAntesDescuentos).toFixed(2)}</strong></div>` : ''}
          ${mostrarDescuentos && totalDescuentos > 0 ? `<div style="margin-bottom:5px;color:#d32f2f;">Descuentos: <strong>-$${Number(totalDescuentos).toFixed(2)}</strong></div>` : ''}
          <div style="margin-bottom:5px;">Subtotal: <strong>$${Number(subtotal).toFixed(2)}</strong></div>
          ${mostrarImpuestos && impuestos.length > 0 ? `<div style="margin-bottom:5px;">Total Impuestos: <strong>$${Number(totalImpuestos).toFixed(2)}</strong></div>` : ''}
          ${totalNotaCredito > 0 ? `<div style="margin-bottom:5px;">Nota(s) de crédito: <strong>-$${Number(totalNotaCredito).toFixed(2)}</strong></div>` : ''}
          <div class="total-final">
            TOTAL: $${Number(totalAPagar).toFixed(2)}
          </div>
        </div>`,
          facturaData
        )}
        ${config.leyendaPiePagina ? `<div style="margin-top:25px;padding-top:15px;border-top:1px solid #ddd;font-size:11px;color:#666;text-align:center;">${config.leyendaPiePagina}</div>` : ''}
        ${config.mensajeGracias ? `<div style="margin-top:10px;font-size:12px;text-align:center;font-weight:600;">${config.mensajeGracias}</div>` : ''}
        ${config.mensajeGarantia ? `<div style="margin-top:8px;font-size:11px;text-align:center;color:#555;">${config.mensajeGarantia}</div>` : ''}
        ${config.mensajeDevolucion ? `<div style="margin-top:8px;font-size:11px;text-align:center;color:#555;">${config.mensajeDevolucion}</div>` : ''}
        <div style="margin-top:30px;padding-top:20px;border-top:1px solid #ddd;font-size:12px;">
          <div style="border-bottom:1px solid #333;width:35%;min-height:18px;margin-bottom:6px;"></div>
          <div>Preparado por:</div>
          <div></div>
          <div></div>
          ${mostrarObservaciones ? `<div style="margin-top:6px;">Observaciones: ${observaciones}</div>` : ''}
        </div>
        ${facturaData.qrDataUrl ? scriptQrCartaSinSalto : ''}
      </body>
    </html>
  `;
};

/**
 * PLANTILLA 4: Formato carta 8.5x11 - Estilo moderno con colores
 */
export const plantillaCartaModerna = (facturaData) => {
  const {
    items = [],
    impuestos = [],
    subtotal = 0,
    totalDescuentos = 0,
    totalImpuestos = 0,
    total = 0,
    documento = '',
    numero = '',
    fecha = '',
    ncf = '',
    tipoDocumento = '',
    observaciones = '',
    empresa = {},
    configuracion = {},
    cliente_nombre = '',
  } = facturaData;

  const totalNotaCredito = facturaData.totalNotaCredito ?? facturaData.total_notas_credito ?? 0;
  const totalAPagar = facturaData.totalAPagar ?? Math.max(0, Number(total) - Number(totalNotaCredito));

  const config = configuracion || {};
  const {
    mostrarInfoEmpresa,
    empresaNombre,
    empresaDireccion,
    empresaTelefono,
    empresaRNC,
    empresaEmail,
  } = resolverDatosEmpresa(empresa, config);
  const mostrarNCF = config.mostrarNCF !== false;
  const mostrarObservaciones = config.mostrarObservaciones !== false && observaciones;
  const mostrarDescuentos = config.mostrarDescuentos !== false;
  const mostrarImpuestos = config.mostrarImpuestos !== false;
  const mostrarSubtotalAntesDescuentos = config.mostrarSubtotalAntesDescuentos !== false;

  const formatoFecha = formatoFechaLargo;

  const itemsHtml = items.length === 0 
    ? '<tr><td colspan="6" style="text-align:center;padding:20px;">Sin ítems</td></tr>'
    : items.map((item, idx) => `
        <tr style="background-color: ${idx % 2 === 0 ? '#ffffff' : '#f9f9f9'};">
          <td style="padding:10px;border-bottom:1px solid #e0e0e0;">${String(item.codigo || '—')}</td>
          <td style="padding:10px;border-bottom:1px solid #e0e0e0;">${String(item.descripcion || '—')}${subdescripcionHtml(item)}</td>
          <td style="text-align:center;padding:10px;border-bottom:1px solid #e0e0e0;">${parseFloat(item.cantidad || 0)}</td>
          <td style="text-align:right;padding:10px;border-bottom:1px solid #e0e0e0;">$${parseFloat(item.precio || 0).toFixed(2)}</td>
          <td style="text-align:right;padding:10px;border-bottom:1px solid #e0e0e0;">$${parseFloat(item.descuento || 0).toFixed(2)}</td>
          <td style="text-align:right;padding:10px;border-bottom:1px solid #e0e0e0;font-weight:600;">$${parseFloat(item.subtotal || 0).toFixed(2)}</td>
        </tr>
      `).join('');

  const impuestosHtml = impuestos.length > 0
    ? impuestos.map(imp => `
        <tr style="background-color:#f5f5f5;">
          <td colspan="5" style="text-align:right;padding:10px;border-bottom:1px solid #e0e0e0;"><strong>${imp.nombre}:</strong></td>
          <td style="text-align:right;padding:10px;border-bottom:1px solid #e0e0e0;"><strong>$${Number(imp.monto || 0).toFixed(2)}</strong></td>
        </tr>
      `).join('')
    : '';

  const subtotalAntesDescuentos = items.reduce(
    (s, i) => s + parseFloat(i.cantidad || 0) * parseFloat(i.precio || 0),
    0
  );

  return `
    <!DOCTYPE html>
    <html>
      <head>
        <title>Factura ${documento}-${numero}</title>
        <style>
          @page {
            size: letter;
            margin: 15mm;
          }
          @media print {
            body { 
              margin: 0 !important;
              padding: 0 !important;
            }
            * { box-sizing: border-box; }
          }
          ${cssQrCarta}
          body { 
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            font-size: 13px;
            padding: 15mm;
            margin: 0;
            color: #2c3e50;
            line-height: 1.6;
          }
          .header {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            padding: 28px 30px;
            border-radius: 8px;
            margin-bottom: 30px;
          }
          .tabla-encabezado {
            width: 100%;
            border-collapse: collapse;
            margin: 0;
          }
          .tabla-encabezado td {
            vertical-align: top;
            border: none;
            padding: 0;
            color: white;
          }
          .info-box {
            background-color: #f8f9fa;
            padding: 20px;
            border-radius: 8px;
            margin-bottom: 25px;
            border-left: 4px solid #667eea;
          }
          table.items {
            width: 100%;
            border-collapse: collapse;
            margin: 25px 0;
            border-radius: 8px;
            overflow: hidden;
            box-shadow: 0 2px 8px rgba(0,0,0,0.1);
          }
          table.items th {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            padding: 15px;
            text-align: left;
            font-weight: 600;
          }
          .totales-box {
            background-color: #f8f9fa;
            padding: 20px;
            border-radius: 8px;
            margin-top: 30px;
            text-align: right;
          }
          .cierre-doc .totales-box { margin-top: 0; }
          .total-final {
            font-size: 24px;
            font-weight: 700;
            color: #667eea;
            margin-top: 15px;
            padding-top: 15px;
            border-top: 3px solid #667eea;
          }
        </style>
      </head>
      <body>
        <div class="header">
          <table class="tabla-encabezado">
            <tr>
              <td style="width:58%;text-align:left;">
                ${mostrarInfoEmpresa ? `
                  <div style="font-size:28px;font-weight:700;margin:0 0 8px 0;line-height:1.2;color:white;">${empresaNombre}</div>
                  ${empresaDireccion ? `<div style="margin:2px 0;font-size:14px;opacity:0.95;color:white;">${empresaDireccion}</div>` : ''}
                  ${empresaTelefono ? `<div style="margin:2px 0;font-size:13px;opacity:0.95;color:white;">Tel: ${empresaTelefono}</div>` : ''}
                  ${empresaRNC ? `<div style="margin:2px 0;font-size:13px;opacity:0.95;color:white;">RNC: ${empresaRNC}</div>` : ''}
                  ${empresaEmail ? `<div style="margin:2px 0;font-size:13px;opacity:0.95;color:white;">${empresaEmail}</div>` : ''}
                ` : '&nbsp;'}
              </td>
              <td style="width:42%;text-align:right;font-size:13px;line-height:1.55;color:white;">
                <div style="font-size:14px;font-weight:700;letter-spacing:0.06em;text-transform:uppercase;margin-bottom:6px;opacity:0.9;">${(tipoDocumento.split(':')[0] || 'FACTURA').trim()}</div>
                ${documento || numero ? `<div style="font-size:20px;font-weight:700;margin-bottom:10px;">${documento}${documento && numero ? '-' : ''}${numero}</div>` : ''}
                <div>Fecha: ${formatoFecha(fecha)}</div>
                ${mostrarNCF && ncf ? `<div>Comprobante (NCF): ${ncf}</div>` : ''}
                ${facturaData.mostrarComprobanteElectronico && facturaData.fechaVencimiento ? `<div>Fecha de validez: ${facturaData.fechaVencimiento}</div>` : ''}
              </td>
            </tr>
          </table>
        </div>
        ${htmlClienteTicket(facturaData)
          ? `<div style="margin:0 0 16px 0;padding:12px 14px;background:#f7f8fa;border-radius:6px;font-size:13px;color:#222;">${htmlClienteTicket(facturaData, 'margin:2px 0;')}</div>`
          : ''}
        <table class="items">
          <thead>
            <tr>
              <th>Código</th>
              <th>Descripción</th>
              <th style="text-align:center;">Cantidad</th>
              <th style="text-align:right;">Precio Unit.</th>
              <th style="text-align:right;">Descuento</th>
              <th style="text-align:right;">Subtotal</th>
            </tr>
          </thead>
          <tbody>
            ${itemsHtml}
            ${mostrarImpuestos ? impuestosHtml : ''}
          </tbody>
        </table>
        ${envolverCierreCarta(
          `<div class="totales-box">
          ${mostrarSubtotalAntesDescuentos ? `<div style="margin-bottom:8px;font-size:14px;">Subtotal (antes descuentos): <strong>$${Number(subtotalAntesDescuentos).toFixed(2)}</strong></div>` : ''}
          ${mostrarDescuentos && totalDescuentos > 0 ? `<div style="margin-bottom:8px;font-size:14px;color:#e74c3c;">Descuentos: <strong>-$${Number(totalDescuentos).toFixed(2)}</strong></div>` : ''}
          <div style="margin-bottom:8px;font-size:14px;">Subtotal: <strong>$${Number(subtotal).toFixed(2)}</strong></div>
          ${mostrarImpuestos && impuestos.length > 0 ? `<div style="margin-bottom:8px;font-size:14px;">Total Impuestos: <strong>$${Number(totalImpuestos).toFixed(2)}</strong></div>` : ''}
          ${totalNotaCredito > 0 ? `<div style="margin-bottom:8px;font-size:14px;">Nota(s) de crédito: <strong>-$${Number(totalNotaCredito).toFixed(2)}</strong></div>` : ''}
          <div class="total-final">
            TOTAL: $${Number(totalAPagar).toFixed(2)}
          </div>
        </div>`,
          facturaData
        )}
        ${config.leyendaPiePagina ? `<div style="margin-top:25px;padding-top:15px;border-top:1px solid rgba(255,255,255,0.3);font-size:12px;text-align:center;opacity:0.9;">${config.leyendaPiePagina}</div>` : ''}
        ${config.mensajeGracias ? `<div style="margin-top:12px;font-size:14px;text-align:center;font-weight:600;">${config.mensajeGracias}</div>` : ''}
        ${config.mensajeGarantia ? `<div style="margin-top:8px;font-size:12px;text-align:center;opacity:0.9;">${config.mensajeGarantia}</div>` : ''}
        ${config.mensajeDevolucion ? `<div style="margin-top:8px;font-size:12px;text-align:center;opacity:0.9;">${config.mensajeDevolucion}</div>` : ''}
        <div style="margin-top:30px;padding-top:20px;border-top:1px solid rgba(255,255,255,0.3);font-size:13px;">
          <div style="border-bottom:1px solid rgba(255,255,255,0.8);width:35%;min-height:18px;margin-bottom:6px;"></div>
          <div>Preparado por:</div>
          <div></div>
          <div></div>
          ${mostrarObservaciones ? `<div style="margin-top:6px;">Observaciones: ${observaciones}</div>` : ''}

        </div>
        ${facturaData.qrDataUrl ? scriptQrCartaSinSalto : ''}
      </body>
    </html>
  `;
};

/**
 * PLANTILLA 5: Formato carta 8.5x11 - Estilo minimalista elegante
 */
export const plantillaCartaMinimalista = (facturaData) => {
  const {
    items = [],
    impuestos = [],
    subtotal = 0,
    totalDescuentos = 0,
    totalImpuestos = 0,
    total = 0,
    documento = '',
    numero = '',
    fecha = '',
    ncf = '',
    tipoDocumento = '',
    observaciones = '',
    empresa = {},
    configuracion = {},
    cliente_nombre = '',
  } = facturaData;

  
  const totalNotaCredito = facturaData.totalNotaCredito ?? facturaData.total_notas_credito ?? 0;
  const totalAPagar = facturaData.totalAPagar ?? Math.max(0, Number(total) - Number(totalNotaCredito));

  const config = configuracion || {};
  const {
    mostrarInfoEmpresa,
    empresaNombre,
    empresaDireccion,
    empresaTelefono,
    empresaRNC,
    empresaEmail,
  } = resolverDatosEmpresa(empresa, config);
  const mostrarNCF = config.mostrarNCF !== false;
  const mostrarObservaciones = config.mostrarObservaciones !== false && observaciones;
  const mostrarDescuentos = config.mostrarDescuentos !== false;
  const mostrarImpuestos = config.mostrarImpuestos !== false;
  const mostrarSubtotalAntesDescuentos = config.mostrarSubtotalAntesDescuentos !== false;

  const formatoFecha = formatoFechaLargo;

  const itemsHtml = items.length === 0 
    ? '<tr><td colspan="6" style="text-align:center;padding:30px;color:#999;">Sin ítems</td></tr>'
    : items.map(item => `
        <tr>
          <td style="padding:12px 8px;border-bottom:1px solid #e8e8e8;">${String(item.codigo || '—')}</td>
          <td style="padding:12px 8px;border-bottom:1px solid #e8e8e8;">${String(item.descripcion || '—')}${subdescripcionHtml(item)}</td>
          <td style="text-align:center;padding:12px 8px;border-bottom:1px solid #e8e8e8;">${parseFloat(item.cantidad || 0)}</td>
          <td style="text-align:right;padding:12px 8px;border-bottom:1px solid #e8e8e8;">$${parseFloat(item.precio || 0).toFixed(2)}</td>
          <td style="text-align:right;padding:12px 8px;border-bottom:1px solid #e8e8e8;">$${parseFloat(item.descuento || 0).toFixed(2)}</td>
          <td style="text-align:right;padding:12px 8px;border-bottom:1px solid #e8e8e8;">$${parseFloat(item.subtotal || 0).toFixed(2)}</td>
        </tr>
      `).join('');

  const impuestosHtml = impuestos.length > 0
    ? impuestos.map(imp => `
        <tr>
          <td colspan="5" style="text-align:right;padding:12px 8px;border-bottom:1px solid #e8e8e8;color:#666;">${imp.nombre}:</td>
          <td style="text-align:right;padding:12px 8px;border-bottom:1px solid #e8e8e8;">$${Number(imp.monto || 0).toFixed(2)}</td>
        </tr>
      `).join('')
    : '';

  const subtotalAntesDescuentos = items.reduce(
    (s, i) => s + parseFloat(i.cantidad || 0) * parseFloat(i.precio || 0),
    0
  );

  return `
    <!DOCTYPE html>
    <html>
      <head>
        <title>Factura ${documento}-${numero}</title>
        <style>
          @page {
            size: letter;
            margin: 25mm;
          }
          @media print {
            body { 
              margin: 0 !important;
              padding: 0 !important;
            }
            * { box-sizing: border-box; }
          }
          ${cssQrCarta}
          body { 
            font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
            font-size: 14px;
            padding: 25mm;
            margin: 0;
            color: #333;
            line-height: 1.8;
          }
          .header {
            margin-bottom: 40px;
            padding-bottom: 28px;
            border-bottom: 1px solid #ddd;
          }
          .tabla-encabezado {
            width: 100%;
            border-collapse: collapse;
            margin: 0;
          }
          .tabla-encabezado td {
            vertical-align: top;
            border: none;
            padding: 0;
          }
          table.items {
            width: 100%;
            border-collapse: collapse;
            margin: 40px 0;
          }
          table.items th {
            padding: 15px 8px;
            text-align: left;
            font-weight: 500;
            font-size: 12px;
            text-transform: uppercase;
            letter-spacing: 1px;
            color: #666;
            border-bottom: 2px solid #ddd;
          }
          .totales {
            margin-top: 50px;
            text-align: right;
            padding-top: 30px;
            border-top: 1px solid #ddd;
          }
          .cierre-doc .totales { margin-top: 0; padding-top: 0; border-top: none; }
          .total-final {
            font-size: 28px;
            font-weight: 300;
            margin-top: 20px;
            padding-top: 20px;
            border-top: 2px solid #333;
            letter-spacing: 1px;
          }
        </style>
      </head>
      <body>
        <div class="header">
          <table class="tabla-encabezado">
            <tr>
              <td style="width:58%;text-align:left;">
                ${mostrarInfoEmpresa ? `
                  <div style="font-size:30px;font-weight:300;letter-spacing:2px;color:#222;margin:0 0 8px 0;line-height:1.2;">${empresaNombre}</div>
                  ${empresaDireccion ? `<div style="margin:2px 0;font-size:13px;color:#666;">${empresaDireccion}</div>` : ''}
                  ${empresaTelefono ? `<div style="margin:2px 0;font-size:12px;color:#666;">Tel: ${empresaTelefono}</div>` : ''}
                  ${empresaRNC ? `<div style="margin:2px 0;font-size:12px;color:#666;">RNC: ${empresaRNC}</div>` : ''}
                  ${empresaEmail ? `<div style="margin:2px 0;font-size:12px;color:#666;">${empresaEmail}</div>` : ''}
                ` : '&nbsp;'}
              </td>
              <td style="width:42%;text-align:right;font-size:13px;line-height:1.7;color:#444;">
                <div style="font-size:12px;font-weight:500;letter-spacing:2px;text-transform:uppercase;color:#666;margin-bottom:6px;">${(tipoDocumento.split(':')[0] || 'FACTURA').trim()}</div>
                ${documento || numero ? `<div style="font-size:22px;font-weight:300;letter-spacing:1px;color:#222;margin-bottom:10px;">${documento}${documento && numero ? '-' : ''}${numero}</div>` : ''}
                <div>Fecha: ${formatoFecha(fecha)}</div>
                ${mostrarNCF && ncf ? `<div>Comprobante (NCF): ${ncf}</div>` : ''}
                ${facturaData.mostrarComprobanteElectronico && facturaData.fechaVencimiento ? `<div>Fecha de validez: ${facturaData.fechaVencimiento}</div>` : ''}
              </td>
            </tr>
          </table>
        </div>
        ${htmlClienteTicket(facturaData)
          ? `<div style="margin:0 0 16px 0;padding:12px 14px;background:#f7f8fa;border-radius:6px;font-size:13px;color:#222;">${htmlClienteTicket(facturaData, 'margin:2px 0;')}</div>`
          : ''}
        <table class="items">
          <thead>
            <tr>
              <th>Código</th>
              <th>Descripción</th>
              <th style="text-align:center;">Cantidad</th>
              <th style="text-align:right;">Precio Unit.</th>
              <th style="text-align:right;">Descuento</th>
              <th style="text-align:right;">Subtotal</th>
            </tr>
          </thead>
          <tbody>
            ${itemsHtml}
            ${mostrarImpuestos ? impuestosHtml : ''}
          </tbody>
        </table>
        ${envolverCierreCarta(
          `<div class="totales">
          ${mostrarSubtotalAntesDescuentos ? `<div style="margin-bottom:10px;font-size:14px;">Subtotal (antes descuentos): $${Number(subtotalAntesDescuentos).toFixed(2)}</div>` : ''}
          ${mostrarDescuentos && totalDescuentos > 0 ? `<div style="margin-bottom:10px;font-size:14px;">Descuentos: -$${Number(totalDescuentos).toFixed(2)}</div>` : ''}
          <div style="margin-bottom:10px;font-size:14px;">Subtotal: $${Number(subtotal).toFixed(2)}</div>
          ${mostrarImpuestos && impuestos.length > 0 ? `<div style="margin-bottom:10px;font-size:14px;">Total Impuestos: $${Number(totalImpuestos).toFixed(2)}</div>` : ''}
          ${totalNotaCredito > 0 ? `<div style="margin-bottom:10px;font-size:14px;">Nota(s) de crédito: -$${Number(totalNotaCredito).toFixed(2)}</div>` : ''}
          <div class="total-final">
            TOTAL $${Number(totalAPagar).toFixed(2)}
          </div>
        </div>`,
          facturaData
        )}
        ${config.leyendaPiePagina ? `<div style="margin-top:40px;padding-top:25px;border-top:1px solid #ddd;font-size:12px;color:#666;text-align:center;line-height:1.6;">${config.leyendaPiePagina}</div>` : ''}
        ${config.mensajeGracias ? `<div style="margin-top:15px;font-size:14px;text-align:center;font-weight:600;">${config.mensajeGracias}</div>` : ''}
        ${config.mensajeGarantia ? `<div style="margin-top:10px;font-size:12px;text-align:center;color:#555;">${config.mensajeGarantia}</div>` : ''}
        ${config.mensajeDevolucion ? `<div style="margin-top:10px;font-size:12px;text-align:center;color:#555;">${config.mensajeDevolucion}</div>` : ''}
        <div style="margin-top:40px;padding-top:25px;border-top:1px solid #ddd;font-size:13px;">
          <div style="border-bottom:1px solid #333;width:35%;min-height:20px;margin-bottom:8px;"></div>
          <div>Preparado por:</div>
          <div></div>
          <div></div>
          ${mostrarObservaciones ? `<div style="margin-top:6px;">Observaciones: ${observaciones}</div>` : ''}
        </div>
        ${facturaData.qrDataUrl ? scriptQrCartaSinSalto : ''}
      </body>
    </html>
  `;
};

/**
 * Objeto con todas las plantillas disponibles
 */
export const plantillas = {
  '80mm-basica': {
    nombre: 'Ticket 80mm - Básico',
    descripcion: 'Recibo térmico 80mm estilo e-CF (COD/ITEM/PRECIO/ITBIS/TOTAL)',
    funcion: plantilla80mmBasica,
    tipo: '80mm'
  },
  '80mm-lineas': {
    nombre: 'Ticket 80mm - Con Líneas',
    descripcion: 'Recibo térmico 80mm estilo e-CF con separadores',
    funcion: plantilla80mmConLineas,
    tipo: '80mm'
  },
  'carta-profesional': {
    nombre: 'Carta 8.5x11 - Profesional',
    descripcion: 'Formato profesional con encabezado formal',
    funcion: plantillaCartaProfesional,
    tipo: 'carta'
  },
  'carta-moderna': {
    nombre: 'Carta 8.5x11 - Moderna',
    descripcion: 'Formato moderno con colores y diseño atractivo',
    funcion: plantillaCartaModerna,
    tipo: 'carta'
  },
  'carta-minimalista': {
    nombre: 'Carta 8.5x11 - Minimalista',
    descripcion: 'Formato elegante y minimalista',
    funcion: plantillaCartaMinimalista,
    tipo: 'carta'
  }
};

/**
 * Función helper para obtener HTML de una plantilla por su ID
 */
export const obtenerPlantilla = (idPlantilla, facturaData) => {
  const plantilla = plantillas[idPlantilla];
  if (!plantilla) {
    console.warn(`Plantilla ${idPlantilla} no encontrada, usando plantilla por defecto`);
    return plantillas['80mm-basica'].funcion(facturaData);
  }
  return plantilla.funcion(facturaData);
};

export default plantillas;
